import { Globe2, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  ShippingRouteStatus,
  type DeliveryMethod,
  type LogisticsIncoterm,
  type Money,
} from '~/graphql/graphql';
import { DeliveryJourneyDiagram } from '~/components/common/DeliveryJourneyDiagram';
import { incotermRequiresAddress, useIncotermLabel } from '~/utils/incoterms';
import { deliveryMethodLabelKey } from '~/utils/deliveryMethod';

type PortLite = {
  id: string;
  name: string;
  city?: string | null;
  country?: string | null;
};

type CartForSummary = {
  deliveryMethod?: DeliveryMethod | null;
  incotermLoading?: LogisticsIncoterm | null;
  incotermDischarge?: LogisticsIncoterm | null;
  incotermAddressDestination?: string | null;
  portOfLoading?: PortLite | null;
  portOfDischarge?: PortLite | null;
  shippingRoute?: {
    id: string;
    status: ShippingRouteStatus;
    forwarder?: string | null;
    transitDays?: number | null;
    freeDays?: number | null;
    freightCost?: Money | null;
    hasForwarderContact?: boolean | null;
  } | null;
};

interface InternationalShippingSummaryProps {
  cart: CartForSummary;
}

/** Read-only checkout recap of the cart's international shipping selections. */
export function InternationalShippingSummary({
  cart,
}: Readonly<InternationalShippingSummaryProps>) {
  const { t } = useTranslation('cart');
  const incotermLabel = useIncotermLabel();

  const portLabel = (port: PortLite | null | undefined) => {
    if (!port) return '—';
    const place = [port.city, port.country].filter(Boolean).join(', ');
    return [port.name, place].filter(Boolean).join(' · ');
  };

  const route = cart.shippingRoute;
  const isPending = route?.status === ShippingRouteStatus.Requested;

  return (
    <div
      data-testid="international-shipping-summary"
      className="bg-neut-bg-alt rounded-lg border border-neut-200 p-6 shadow-sm"
    >
      <div className="flex items-center gap-2 mb-4">
        <Globe2 className="w-5 h-5 text-prim-600" aria-hidden="true" />
        <h2 className="text-neut-prim font-semibold">
          {t('cart.international.title')}
        </h2>
      </div>

      {cart.deliveryMethod && (
        <div className="space-y-2 mb-4">
          <Row
            label={t('cart.international.delivery_terms')}
            value={t(deliveryMethodLabelKey(cart.deliveryMethod))}
          />
          <DeliveryJourneyDiagram method={cart.deliveryMethod} />
        </div>
      )}

      <div className="space-y-2 bg-neut-100 rounded-lg p-4">
        <Row
          label={t('cart.international.incoterm_loading')}
          value={incotermLabel(cart.incotermLoading)}
        />
        <Row
          label={t('cart.international.port_of_loading')}
          value={portLabel(cart.portOfLoading)}
        />
        <Row
          label={t('cart.international.incoterm_discharge')}
          value={incotermLabel(cart.incotermDischarge)}
        />
        <Row
          label={t('cart.international.port_of_discharge')}
          value={portLabel(cart.portOfDischarge)}
        />
        {incotermRequiresAddress(cart.incotermDischarge) &&
          cart.incotermAddressDestination && (
            <Row
              label={t('cart.international.delivery_address')}
              value={cart.incotermAddressDestination}
            />
          )}
      </div>

      <div className="mt-4 pt-4 border-t border-neut-200">
        <p className="text-sm text-neut-600 mb-3 font-medium">
          {t('cart.international.shipping_route')}
        </p>
        <div className="space-y-2 bg-neut-100 rounded-lg p-4">
          {route ? (
            <>
              <Row
                label={t('cart.international.forwarder')}
                value={route.forwarder ?? '—'}
              />
              <Row
                label={t('cart.international.transit')}
                value={
                  route.transitDays != null
                    ? t('cart.international.transit_days', {
                        days: route.transitDays,
                      })
                    : t('cart.international.transit_unknown')
                }
              />
              <Row
                label={t('cart.international.free_days_label')}
                value={
                  route.freeDays != null
                    ? t('cart.international.free_days', {
                        days: route.freeDays,
                      })
                    : '—'
                }
              />
              {/* Not `!hasForwarderContact` — a cached query from before this
                  field existed leaves it undefined, which is unknown, not absent. */}
              {route.hasForwarderContact === false && (
                <p
                  data-testid="forwarder-contact-warning"
                  className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5"
                >
                  <TriangleAlert size={14} className="shrink-0 mt-0.5" />
                  {t('cart.international.no_forwarder_contact')}
                </p>
              )}
              {isPending && (
                <Row
                  label={t('cart.international.status')}
                  value={t('cart.international.pending_review')}
                />
              )}
            </>
          ) : (
            <p className="text-sm text-neut-sec italic">
              {t('cart.international.route_not_selected')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
}: Readonly<{
  label: string;
  value: string;
}>) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="text-neut-600 shrink-0">{label}</span>
      <span className="text-neut-prim font-medium text-right truncate">
        {value}
      </span>
    </div>
  );
}
